import { Button, Checkbox, Input, Modal, Radio } from "antd";
import { ChevronDownIcon, ChevronUpIcon, PlusIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

export interface DownloadItineraryOptions {
  includeImages: boolean;
  pickupAt?: string;
  dropOffAt?: string;
  /** Advanced option: adds a Hotels/Tour/Flights cost-by-category breakdown to the document. */
  includeCostBreakdown?: boolean;
  /** Advanced option: adds each hotel/activity/transport's own rate next to it. */
  includeIndividualRates?: boolean;
  /** Advanced option: appends the tenant back-cover PDF (customer reviews) to a PDF download. */
  includeBackCover?: boolean;
  /** Advanced option: overrides the tenant's default document format for this download only. */
  format?: "pdf" | "docx";
  /** Hotel options to include, in one document; only set when the itinerary has several options. */
  hotelOptions?: string[];
}

interface DownloadItineraryModalProps {
  open: boolean;
  onCancel: () => void;
  onDownload: (options: DownloadItineraryOptions) => void;
  /** The agent's remembered "include images" choice from their last download, if any. Defaults to true. */
  defaultIncludeImages?: boolean;
  /** Called as soon as the agent changes the images choice, so the caller can persist it as their new default. */
  onIncludeImagesChange?: (value: boolean) => void;
  /** Shows the "Include back cover in PDF" advanced option; pass only when the tenant has a back-cover PDF. */
  showBackCoverOption?: boolean;
  /** Initial state of the reviews option, usually the tenant preference. Defaults to true. */
  defaultIncludeBackCover?: boolean;
  /**
   * Agent portal mode: the format choice is shown in the main section, and individual rates and the back cover are
   * never offered. An agent's markup is not set here; the server applies the markup saved on the quote.
   */
  agentMode?: boolean;
  /** Names of the itinerary's hotel options; the option picker appears only when there are two or more. */
  hotelOptionNames?: string[];
  /** Modal title; defaults to "Download Itinerary". */
  title?: string;
  /** Extra inputs shown above the download choices, for example a package's travel date and party. */
  extraFields?: ReactNode;
  /** Hides the pickup and drop-off inputs, for documents that are not tied to a trip (a generic package). */
  hidePickupDropOff?: boolean;
  /** Disables the Download button, for example while an extra field is invalid. */
  downloadDisabled?: boolean;
  /** Shows a loading state on the Download button while the document is generated. */
  downloading?: boolean;
}

/**
 * Renders itinerary download options for images, pickup, and drop off details.
 * @param props Modal visibility and download callback props.
 */
const DownloadItineraryModal = ({
  open,
  onCancel,
  onDownload,
  defaultIncludeImages,
  onIncludeImagesChange,
  showBackCoverOption = false,
  defaultIncludeBackCover,
  agentMode = false,
  hotelOptionNames = [],
  title = "Download Itinerary",
  extraFields,
  hidePickupDropOff = false,
  downloadDisabled = false,
  downloading = false,
}: DownloadItineraryModalProps) => {
  const hasHotelOptions = hotelOptionNames.length > 1;
  const optionKey = hotelOptionNames.join("\u0000");
  const [selectedHotelOptions, setSelectedHotelOptions] = useState<string[]>(hotelOptionNames);
  const [includeImages, setIncludeImages] = useState(defaultIncludeImages ?? true);
  const [showPickup, setShowPickup] = useState(false);
  const [showDropOff, setShowDropOff] = useState(false);
  const [pickupAt, setPickupAt] = useState("");
  const [dropOffAt, setDropOffAt] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [includeCostBreakdown, setIncludeCostBreakdown] = useState(false);
  const [includeIndividualRates, setIncludeIndividualRates] = useState(false);
  const [includeBackCover, setIncludeBackCover] = useState(defaultIncludeBackCover ?? true);
  const [format, setFormat] = useState<"pdf" | "docx" | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    setIncludeImages(defaultIncludeImages ?? true);
    setShowPickup(false);
    setShowDropOff(false);
    setPickupAt("");
    setDropOffAt("");
    setShowAdvanced(false);
    setIncludeCostBreakdown(false);
    setIncludeIndividualRates(false);
    setIncludeBackCover(defaultIncludeBackCover ?? true);
    setFormat(undefined);
    setSelectedHotelOptions(hotelOptionNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, optionKey]);

  const handleIncludeImagesChange = (value: boolean) => {
    setIncludeImages(value);
    onIncludeImagesChange?.(value);
  };

  const handleDownload = () => {
    onDownload({
      includeImages,
      pickupAt: hidePickupDropOff ? undefined : pickupAt.trim() || undefined,
      dropOffAt: hidePickupDropOff ? undefined : dropOffAt.trim() || undefined,
      includeCostBreakdown,
      includeIndividualRates,
      ...(showBackCoverOption ? { includeBackCover } : {}),
      format,
      ...(hasHotelOptions ? { hotelOptions: hotelOptionNames.filter((name) => selectedHotelOptions.includes(name)) } : {}),
    });
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={handleDownload}
      okText="Download"
      okButtonProps={{ disabled: downloadDisabled || (hasHotelOptions && selectedHotelOptions.length === 0), loading: downloading }}
    >
      <div className="space-y-4">
        {extraFields}
        <div>
          <p className="mb-3 text-slate-600">Include hotel and activity images in the document?</p>
          <Radio.Group value={includeImages} onChange={(e) => handleIncludeImagesChange(e.target.value)}>
            <Radio value={true}>Yes, include decorative images</Radio>
            <Radio value={false}>No, skip images</Radio>
          </Radio.Group>
        </div>

        {hasHotelOptions && (
          <div>
            <p className="mb-1 text-slate-600">Hotel options to include</p>
            <Checkbox.Group
              className="flex flex-col gap-1"
              aria-label="Hotel options to include"
              value={selectedHotelOptions}
              onChange={(values) => setSelectedHotelOptions(values as string[])}
              options={hotelOptionNames.map((name) => ({ label: name, value: name }))}
            />
            <p className="mt-1 text-xs text-slate-500">
              Selected options are combined in one document, each labelled with its name and priced separately.
            </p>
            {selectedHotelOptions.length === 0 && <p className="mt-1 text-xs text-red-600">Select at least one hotel option.</p>}
          </div>
        )}

        {agentMode && (
          <div>
            <p className="mb-3 text-slate-600">Document format</p>
            <Radio.Group
              value={format ?? "default"}
              onChange={(e) => setFormat(e.target.value === "default" ? undefined : e.target.value)}
              aria-label="Document format"
            >
              <Radio value="default">Default</Radio>
              <Radio value="pdf">PDF</Radio>
              <Radio value="docx">Word (.docx)</Radio>
            </Radio.Group>
            {format === "docx" && (
              <p className="mt-2 text-xs text-slate-500">A Word file can be edited before you send it to your client.</p>
            )}
          </div>
        )}

        <div className={hidePickupDropOff ? "hidden" : "flex flex-wrap gap-2"}>
          {!showPickup && (
            <Button icon={<PlusIcon size={16} />} onClick={() => setShowPickup(true)}>
              Add pickup
            </Button>
          )}
          {!showDropOff && (
            <Button icon={<PlusIcon size={16} />} onClick={() => setShowDropOff(true)}>
              Add drop off
            </Button>
          )}
        </div>

        {!hidePickupDropOff && showPickup && (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="download-itinerary-pickup">
              Pickup at
            </label>
            <Input
              id="download-itinerary-pickup"
              value={pickupAt}
              onChange={(event) => setPickupAt(event.target.value)}
              placeholder="Pickup location or time"
              allowClear
            />
          </div>
        )}

        {!hidePickupDropOff && showDropOff && (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="download-itinerary-drop-off">
              Drop off at
            </label>
            <Input
              id="download-itinerary-drop-off"
              value={dropOffAt}
              onChange={(event) => setDropOffAt(event.target.value)}
              placeholder="Drop off location or time"
              allowClear
            />
          </div>
        )}

        <div className="border-t border-slate-200 pt-3">
          <button
            type="button"
            onClick={() => setShowAdvanced((prev) => !prev)}
            className="flex w-full items-center justify-between text-sm font-medium text-slate-700"
          >
            Advanced options
            {showAdvanced ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
          </button>

          {showAdvanced && (
            <div className="mt-3 space-y-3">
              {/* Agents choose the format in the main section; individual rates and the back cover are never offered to them. */}
              {!agentMode && (
                <div>
                  <p className="mb-1 text-sm text-slate-600">Document format</p>
                  <Radio.Group value={format ?? "default"} onChange={(e) => setFormat(e.target.value === "default" ? undefined : e.target.value)}>
                    <Radio value="default">Tenant default</Radio>
                    <Radio value="pdf">PDF</Radio>
                    <Radio value="docx">Word (.docx)</Radio>
                  </Radio.Group>
                </div>
              )}
              <div>
                <Checkbox
                  checked={includeCostBreakdown}
                  onChange={(event) => setIncludeCostBreakdown(event.target.checked)}
                >
                  {agentMode ? "Include cost breakdown" : "Include cost breakdown for inclusions"}
                </Checkbox>
                {agentMode && (
                  <p className="ml-6 mt-1 text-xs text-slate-500">
                    Adds a separate table splitting the price into hotels, transport, attractions and flights.
                  </p>
                )}
              </div>
              {!agentMode && (
                <div>
                  <Checkbox
                    checked={includeIndividualRates}
                    onChange={(event) => setIncludeIndividualRates(event.target.checked)}
                  >
                    Include individual rates of products added
                  </Checkbox>
                </div>
              )}
              {!agentMode && showBackCoverOption && (
                <div>
                  <Checkbox checked={includeBackCover} onChange={(event) => setIncludeBackCover(event.target.checked)}>
                    Include back cover in PDF
                  </Checkbox>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DownloadItineraryModal;
